import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Mail, MessageSquare, MapPin, Calendar, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLanguage } from "@/context/LanguageContext";
import { quickCelebration } from "@/utils/confetti";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = ({ services, locations }) => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    preferred_location: "",
    preferred_date: "",
    message: "",
    referral_code: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t("contact.nameRequired");
    }
    if (!formData.email.trim()) {
      newErrors.email = t("contact.emailRequired");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t("contact.validEmail");
    }
    if (!formData.phone.trim()) {
      newErrors.phone = t("contact.phoneRequired");
    }
    if (!formData.service) {
      newErrors.service = t("contact.selectServiceError");
    }
    if (!formData.preferred_location) {
      newErrors.preferred_location = t("contact.selectLocationError");
    }
    if (!formData.message.trim()) {
      newErrors.message = t("contact.messageRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitSuccess(false);
    
    if (!validateForm()) {
      toast.error(t("contact.fillAllFields"), {
        icon: <AlertCircle className="w-5 h-5" />,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit the inquiry
      await axios.post(`${API}/inquiries`, formData);
      
      // If there's a referral code, track it
      if (formData.referral_code.trim()) {
        try {
          await axios.post(`${API}/referrals/use`, {
            referral_code: formData.referral_code.trim(),
            new_client_name: formData.name,
            new_client_email: formData.email,
            new_client_phone: formData.phone
          });
        } catch (refError) {
          console.error("Error tracking referral:", refError);
        }
      }
      
      // Trigger confetti celebration
      quickCelebration();
      
      toast.success(t("contact.successToast"), {
        icon: <CheckCircle className="w-5 h-5" />,
        duration: 5000,
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        service: "",
        preferred_location: "",
        preferred_date: "",
        message: "",
        referral_code: "",
      });
      setErrors({});
      setSubmitSuccess(true);
    } catch (error) {
      console.error("Error submitting inquiry:", error);
      toast.error(t("contact.errorToast"), {
        icon: <AlertCircle className="w-5 h-5" />,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClasses = (fieldName) => 
    `form-input ${errors[fieldName] ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : ''}`;

  return (
    <section
      id="contact"
      className="section-padding bg-muted"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="font-mono-label text-xs text-primary tracking-widest">
            {t("contact.title")}
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold mt-4 mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("contact.heading")} <span className="italic text-primary">{t("contact.headingAccent")}</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t("contact.description")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <h3
                className="text-2xl font-semibold mb-6"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {t("contact.contactInfo")}
              </h3>
              
              <div className="space-y-4">
                <a
                  href="sms:613-850-0805?body=Hi, I'd like to book an appointment"
                  className="flex items-center gap-4 p-4 bg-card rounded-xl border border-border hover:border-primary/30 transition-colors"
                  data-testid="contact-phone"
                >
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <MessageSquare className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{language === "fr" ? "Textez-nous" : "Text Us"}</p>
                    <p className="font-medium">613-850-0805</p>
                  </div>
                </a>

                <a
                  href="mailto:inquiries@karinelauzon.com"
                  className="flex items-center gap-4 p-4 bg-card rounded-xl border border-border hover:border-primary/30 transition-colors"
                  data-testid="contact-email"
                >
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{language === "fr" ? "Courriel" : "Email"}</p>
                    <p className="font-medium">inquiries@karinelauzon.com</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-4 bg-card rounded-xl border border-border">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Calendar className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{language === "fr" ? "Heures" : "Hours"}</p>
                    <p className="font-medium">{language === "fr" ? "Lun-Ven: 10h - 18h" : "Mon-Fri: 10AM - 6PM"}</p>
                    <p className="text-sm text-muted-foreground">{language === "fr" ? "Sam: 9h - 13h" : "Sat: 9AM - 1PM"}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* All Locations */}
            <div>
              <h3
                className="text-2xl font-semibold mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {t("contact.ourLocations")}
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                {t("contact.toBookAppointment")}
              </p>
              
              <div className="space-y-4">
                <a 
                  href="https://tinyurl.com/Book-Carleton-Place"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-4 bg-card rounded-xl border border-border hover:border-primary/50 hover:shadow-md transition-all"
                  data-testid="book-carleton-place"
                >
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div className="flex-grow">
                      <p className="font-semibold text-primary">Carleton Place</p>
                      <p className="text-sm">163 Bridge Street, Downtown</p>
                      <p className="text-xs text-muted-foreground">{t("contact.downtown")}</p>
                      <p className="text-xs text-muted-foreground mt-1">{language === "fr" ? "Lun-Ven: 10h-18h, Sam: 9h-13h" : "Mon-Fri: 10AM-6PM, Sat: 9AM-1PM"}</p>
                    </div>
                    <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full whitespace-nowrap">{t("contact.bookOnline")} →</span>
                  </div>
                </a>

                <a 
                  href="https://karine-lauzon-rn.square.site/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-4 bg-card rounded-xl border border-border hover:border-primary/50 hover:shadow-md transition-all"
                  data-testid="book-kanata"
                >
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div className="flex-grow">
                      <p className="font-semibold text-primary">Kanata</p>
                      <p className="text-sm">320 March Road, Sixth Floor, Clinic 603</p>
                      <p className="text-xs text-muted-foreground">{t("contact.sixthFloor")}</p>
                      <p className="text-xs text-muted-foreground mt-1">{t("contact.byAppointment")}</p>
                    </div>
                    <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full whitespace-nowrap">{t("contact.bookOnline")} →</span>
                  </div>
                </a>

                <a 
                  href="https://karine-lauzon-rn-ottawa.square.site/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-4 bg-card rounded-xl border border-border hover:border-primary/50 hover:shadow-md transition-all"
                  data-testid="book-ottawa"
                >
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div className="flex-grow">
                      <p className="font-semibold text-primary">Ottawa</p>
                      <p className="text-sm">1390 Prince of Wales Drive, Main Floor</p>
                      <p className="text-xs text-muted-foreground">{t("contact.mainFloor")}</p>
                      <p className="text-xs text-muted-foreground mt-1">{t("contact.byAppointment")}</p>
                    </div>
                    <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full whitespace-nowrap">{t("contact.bookOnline")} →</span>
                  </div>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3">
            {submitSuccess ? (
              <div className="bg-card rounded-2xl p-8 md:p-12 border border-border text-center" data-testid="success-message">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-semibold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {t("contact.thankYou")}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {t("contact.submittedSuccess")}
                </p>
                <Button
                  onClick={() => setSubmitSuccess(false)}
                  className="btn-outline"
                  data-testid="submit-another"
                >
                  {t("contact.submitAnother")}
                </Button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-card rounded-2xl p-6 md:p-8 border border-border"
                data-testid="contact-form"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Name */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      {t("contact.name")} *
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t("contact.name")}
                      className={inputClasses('name')}
                      data-testid="input-name"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-xs mt-1" data-testid="error-name">{errors.name}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      {t("contact.email")} *
                    </label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className={inputClasses('email')}
                      data-testid="input-email"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-xs mt-1" data-testid="error-email">{errors.email}</p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      {t("contact.phone")} *
                    </label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(613) 850-0805"
                      className={inputClasses('phone')}
                      data-testid="input-phone"
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-xs mt-1" data-testid="error-phone">{errors.phone}</p>
                    )}
                  </div>

                  {/* Service */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      {t("contact.service")} *
                    </label>
                    <Select
                      value={formData.service}
                      onValueChange={(value) => handleSelectChange("service", value)}
                    >
                      <SelectTrigger 
                        className={inputClasses('service')} 
                        data-testid="select-service"
                      >
                        <SelectValue placeholder={t("contact.selectService")} />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service.id} value={service.name}>
                            {service.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.service && (
                      <p className="text-red-500 text-xs mt-1" data-testid="error-service">{errors.service}</p>
                    )}
                  </div>

                  {/* Location */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      {t("contact.location")} *
                    </label>
                    <Select
                      value={formData.preferred_location}
                      onValueChange={(value) => handleSelectChange("preferred_location", value)}
                    >
                      <SelectTrigger 
                        className={inputClasses('preferred_location')} 
                        data-testid="select-location"
                      >
                        <SelectValue placeholder={t("contact.selectLocation")} />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((location) => (
                          <SelectItem key={location.id} value={location.name}>
                            {location.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.preferred_location && (
                      <p className="text-red-500 text-xs mt-1" data-testid="error-location">{errors.preferred_location}</p>
                    )}
                  </div>

                  {/* Preferred Date */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      {t("contact.date")}
                    </label>
                    <Input
                      name="preferred_date"
                      type="date"
                      value={formData.preferred_date}
                      onChange={handleChange}
                      className="form-input"
                      data-testid="input-date"
                    />
                  </div>
                </div>

                {/* Message */}
                <div className="mt-6">
                  <label className="text-sm font-medium mb-2 block">
                    {t("contact.message")} *
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={t("contact.messagePlaceholder")}
                    className={`form-textarea min-h-[120px] ${errors.message ? 'border-red-500 focus:ring-red-200 focus:border-red-500' : ''}`}
                    data-testid="input-message"
                  />
                  {errors.message && (
                    <p className="text-red-500 text-xs mt-1" data-testid="error-message">{errors.message}</p>
                  )}
                </div>

                {/* Referral Code */}
                <div className="mt-6">
                  <label className="text-sm font-medium mb-2 block">
                    {language === "fr" ? "Référé par (optionnel)" : "Referred by (optional)"}
                  </label>
                  <Input
                    name="referral_code"
                    value={formData.referral_code}
                    onChange={handleChange}
                    placeholder={language === "fr" ? "Entrez le nom ou code de référence" : "Enter referrer's name or code"}
                    className="form-input"
                    data-testid="input-referral-code"
                  />
                  <p className="text-muted-foreground text-xs mt-1">
                    {language === "fr" 
                      ? "Vous avez été référé par un ami? Entrez son nom pour que vous receviez chacun 25$!"
                      : "Were you referred by a friend? Enter their name so you both get $50 after your first treatment!"}
                  </p>
                </div>

                {/* Submit Button */}
                <div className="mt-8">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-accent w-full md:w-auto text-lg px-12 py-6"
                    data-testid="submit-inquiry"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        {t("contact.sending")}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        {t("contact.submit")}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
